import React from 'react';
import { connect } from 'react-redux';

import { baseMapStateToProps } from '../../componentBase';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import {
  createAfterEffectsChapter,
  getAfterEffectsChapters,
  deleteAfterEffectsChapter,
  editAfterEffectsChapter,
} from '../../../api/clipGenV2/afterEffectsChapters.actions';
import { callApi } from '../../../helpers/callApi/callApi';
import * as NotificationHelper from '../../../helpers/notificationHelper';
import { createModalOpenAction, createModalCloseAction } from '../../../actions/modalActions';
import { SimpleModalId } from '../../../constants/modalId';
import { generateSequentialNumberOptions } from '../../../helpers/optionsHelper';
import { getAfterEffectsParticipants } from '../../../api/clipGenV2/afterEffectsParticipants.actions';

import { ChaptersTable } from './chaptersTable';
import {
  CreateChapterFormDataProps,
  EditChapterFormDataProps,
  ChapterManagementProps,
  ChaptersRequestStates,
} from './chapter.types';
import { CreateChapterFormContainer } from './forms/createChapterFormContainer';
import { EditChapterFormContainer } from './forms/editChapterFormContainer';

export class ChapterManagement extends React.Component<ChapterManagementProps, ChaptersRequestStates> {
  state: ChaptersRequestStates = {
    chapters: [],
    participants: [],
    getChaptersLoading: false,
    getChaptersError: false,
    getParticipantsLoading: false,
    getParticipantsError: false,
    createChapterLoading: false,
    deleteChapterLoading: false,
    chapterToUpdate: null,
    editChapterLoading: false,
  };

  componentDidMount() {
    this.getChapters();
    this.getParticipants();
  }

  getChapters = () => {
    const { getAEChapters } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, getChaptersLoading: true }));

        return getAEChapters();
      },
      onSuccess: ({ payload }) => {
        this.setState(prev => ({ ...prev, chapters: payload, getChaptersLoading: false, getChaptersError: false }));
      },
      onFailure: () =>
        this.setState(prev => ({ ...prev, chapters: [], getChaptersLoading: false, getChaptersError: true })),
    });
  };

  getParticipants = () => {
    const { getAEParticipants } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, getParticipantsLoading: true }));

        return getAEParticipants();
      },
      onSuccess: ({ payload }) => {
        this.setState(prev => ({
          ...prev,
          participants: payload,
          getParticipantsLoading: false,
          getParticipantsError: false,
        }));
      },
      onFailure: () =>
        this.setState(prev => ({
          ...prev,
          participants: [],
          getParticipantsLoading: false,
          getParticipantsError: true,
        })),
    });
  };

  createChapter = (chapter: CreateChapterFormDataProps) => {
    const { createAEChapter, closeCreateChapterModal } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, createChapterLoading: true }));

        return createAEChapter(chapter);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, createChapterLoading: false }));
        closeCreateChapterModal();
        NotificationHelper.success('Successfully created chapter!');
        this.getChapters();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, createChapterLoading: false }));
        NotificationHelper.error('Failed to create chapter!');
      },
    });
  };

  deleteChapter = (chapterId: string) => {
    const { deleteAEChapter } = this.props;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, deleteChapterLoading: true }));

        return deleteAEChapter(chapterId);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, deleteChapterLoading: false }));
        NotificationHelper.success('Successfully deleted chapter!');
        this.getChapters();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, deleteChapterLoading: false }));
        NotificationHelper.error('Failed to delete chapter!');
      },
    });
  };

  editChapter = (chapterNewValues: EditChapterFormDataProps) => {
    const { chapterToUpdate } = this.state;
    const { editAEChapter, closeEditChapterModal } = this.props;
    const chapterId = chapterToUpdate.id;
    const body = chapterNewValues;
    delete body.id;

    callApi({
      action: () => {
        this.setState(prev => ({ ...prev, editChapterLoading: true }));

        return editAEChapter(chapterId, body);
      },
      onSuccess: () => {
        this.setState(prev => ({ ...prev, editChapterLoading: false }));
        closeEditChapterModal();
        NotificationHelper.success('Successfully updated chapter!');
        this.getChapters();
      },
      onFailure: () => {
        this.setState(prev => ({ ...prev, editChapterLoading: false }));
        NotificationHelper.error('Failed to update chapter!');
      },
    });
  };

  handleOpenEditChapterModal = (chapterToUpdate: EditChapterFormDataProps) => {
    const { openEditChapterModal } = this.props;
    this.setState(prev => ({ ...prev, chapterToUpdate }));
    openEditChapterModal();
  };

  render = () => {
    const {
      chapters,
      participants,
      getChaptersLoading,
      getChaptersError,
      createChapterLoading,
      deleteChapterLoading,
      chapterToUpdate,
      editChapterLoading,
    } = this.state;
    const highestOrderIndex = participants.length + chapters.length + 1;
    const orderIndexOptions = generateSequentialNumberOptions(highestOrderIndex);

    if (getChaptersError) {
      return <div>Something went wrong</div>;
    }

    if (!getChaptersLoading) {
      const isLoading = getChaptersLoading || createChapterLoading || deleteChapterLoading || editChapterLoading;

      return (
        <>
          <CreateChapterFormContainer
            isLoading={createChapterLoading}
            onSubmit={this.createChapter}
            orderIndexOptions={orderIndexOptions}
            initialValues={{ orderIndex: highestOrderIndex }}
          />
          {chapterToUpdate && (
            <EditChapterFormContainer
              isLoading={editChapterLoading}
              onSubmit={this.editChapter}
              orderIndexOptions={orderIndexOptions}
              initialValues={chapterToUpdate}
            />
          )}
          <ChaptersTable
            chapters={chapters}
            isLoading={isLoading}
            onClickDelete={this.deleteChapter}
            onClickEdit={this.handleOpenEditChapterModal}
          />
        </>
      );
    }

    return null;
  };
}

const mapDispatchToProps = (dispatch: Function) => ({
  getAEChapters: () => dispatch(getAfterEffectsChapters()),
  getAEParticipants: () => dispatch(getAfterEffectsParticipants()),
  createAEChapter: (chapter: CreateChapterFormDataProps) => dispatch(createAfterEffectsChapter(chapter)),
  deleteAEChapter: (chapterId: string) => dispatch(deleteAfterEffectsChapter(chapterId)),
  editAEChapter: (chapterId: string, chapter: EditChapterFormDataProps) =>
    dispatch(editAfterEffectsChapter(chapterId, chapter)),
  closeCreateChapterModal: () => dispatch(createModalCloseAction(SimpleModalId.CreateAfterEffectsChapterModal)),
  openEditChapterModal: () => dispatch(createModalOpenAction(SimpleModalId.EditAfterEffectsChapterModal)),
  closeEditChapterModal: () => dispatch(createModalCloseAction(SimpleModalId.EditAfterEffectsChapterModal)),
});

const mapStateToProps = (state: ClippingPlatformState) => baseMapStateToProps(state);

export const ChapterManagementContainer = connect(mapStateToProps, mapDispatchToProps)(ChapterManagement);
